// Scènes (petites cinématiques) : rangées avec leur carte, jouées pour un seul joueur par une étape de quête
// « Assister à une scène ». Des acteurs (PNJ qui ne prêtent que leur nom et leur apparence, monstres d'une espèce, ou
// le joueur lui-même)
// apparaissent, marchent, parlent, montrent une émotion, se battent et disparaissent, une action après l'autre ; le
// joueur ne peut pas bouger pendant ce temps. Les acteurs ne sont vus que de lui, et traversent les autres joueurs.
// Un PNJ qui se tient déjà sur la carte joue lui-même son rôle, sans apparaître : déplacé par la scène, il reste où elle
// l'a laissé (pour ce joueur), sauf s'il en est reparti.
import type { TrainerItem, TrainerLevel, TrainerMonster } from './admin-api.ts';
import type { Direction, Position } from './protocol.ts';

/** Émotion montrée dans une bulle au-dessus d'un acteur. */
export type Emote = 'exclaim' | 'question' | 'music' | 'angry' | 'heart' | 'sleep' | 'dots';
export const EMOTES: Readonly<Record<Emote, { label: string; glyph: string }>> = {
  exclaim: { label: 'Surprise', glyph: '!' },
  question: { label: 'Question', glyph: '?' },
  music: { label: 'Joie', glyph: '♪' },
  angry: { label: 'Colère', glyph: '💢' },
  heart: { label: 'Amour', glyph: '♥' },
  sleep: { label: 'Sommeil', glyph: 'z' },
  dots: { label: 'Silence', glyph: '…' },
};
export const isEmote = (value: unknown): value is Emote => typeof value === 'string' && Object.hasOwn(EMOTES, value);
/** Durée d'affichage d'une émotion. */
export const EMOTE_MS = 1100;

/** Le joueur, acteur de toutes les scènes. */
export const PLAYER_ACTOR = 'player';

/** Acteur d'une scène : un PNJ (son nom et son apparence), ou un monstre d'une espèce ; l'acteur `player` est le joueur. */
export type SceneActor = { id: string } & ({ npcId: number } | { speciesId: number; shiny: boolean });

/** Effet d'écran : fondu au noir (jusqu'au fondu retour, ou la fin de la scène), retour du fondu, tremblement, flash. */
export type ScreenEffect = 'fade-out' | 'fade-in' | 'shake' | 'flash';
export const SCREEN_EFFECTS: Readonly<Record<ScreenEffect, string>> = { 'fade-out': 'Fondu au noir', 'fade-in': 'Retour du fondu', shake: 'Tremblement', flash: 'Flash' };
export const isScreenEffect = (value: unknown): value is ScreenEffect => typeof value === 'string' && Object.hasOwn(SCREEN_EFFECTS, value);

/**
 * Une action. `together` : elle commence en même temps que la précédente (deux acteurs qui marchent ensemble…) ; la
 * scène passe à la suite une fois toutes les actions du groupe finies.
 */
export type SceneAction = SceneActionBody & { together?: boolean };

type SceneActionBody =
  /** L'acteur apparaît sur cette case, tourné dans ce sens. */
  | { type: 'appear'; actor: string; x: number; y: number; dir: Direction }
  /** L'acteur rejoint chaque étape du chemin tour à tour (en contournant les obstacles) ; `fast` : il se presse. */
  | { type: 'walk'; actor: string; path: Position[]; fast: boolean }
  /** L'acteur se tourne dans un sens, ou vers le joueur. */
  | { type: 'turn'; actor: string; dir: Direction | 'player' }
  /** L'acteur parle (une ligne = une page). */
  | { type: 'say'; actor: string; text: string }
  | { type: 'emote'; actor: string; emote: Emote }
  | { type: 'wait'; ms: number }
  /**
   * L'acteur (un PNJ) se bat avec cette équipe. `mustWin` : perdu, la scène s'arrête là (l'étape de quête reste à
   * faire, la scène se rejoue au prochain passage sur la carte) ; sinon elle continue quelle que soit l'issue.
   */
  | { type: 'fight'; actor: string; team: TrainerMonster[]; level: TrainerLevel; items: TrainerItem[]; mustWin: boolean }
  | { type: 'vanish'; actor: string }
  /** Effet d'écran, pendant `ms`. */
  | { type: 'effect'; effect: ScreenEffect; ms: number };

export type SceneActionType = SceneAction['type'];

/** Les actions regroupées : chaque groupe commence par une action, suivie de celles qui se jouent en même temps qu'elle. */
export function actionGroups(actions: readonly SceneAction[]): SceneAction[][] {
  const groups: SceneAction[][] = [];
  for (const action of actions) {
    const last = groups.at(-1);
    if (action.together && last) last.push(action); else groups.push([action]);
  }
  return groups;
}

export const SCENE_ACTIONS: Readonly<Record<SceneActionType, { label: string; hint: string }>> = {
  appear: { label: 'Apparaître', hint: 'cliquez sur la carte pour placer l\'acteur' },
  walk: { label: 'Marcher', hint: 'cliquez sur la carte pour ajouter une étape ; clic droit : retirer la dernière' },
  turn: { label: 'Se tourner', hint: '' },
  say: { label: 'Dire', hint: '' },
  emote: { label: 'Émotion', hint: '' },
  wait: { label: 'Attendre', hint: '' },
  fight: { label: 'Combat', hint: '' },
  vanish: { label: 'Disparaître', hint: '' },
  effect: { label: 'Effet d\'écran', hint: '' },
};

/** Actions possibles pour le joueur (il ne se bat pas contre lui-même, ne disparaît pas). */
export const PLAYER_ACTIONS: readonly SceneActionType[] = ['walk', 'turn', 'say', 'emote'];
/** Actions possibles pour un monstre (il ne mène pas de combat de dresseur). */
export const MONSTER_ACTIONS: readonly SceneActionType[] = ['appear', 'walk', 'turn', 'say', 'emote', 'vanish'];

/** L'acteur est-il un monstre ? */
export const isMonsterActor = (actor: SceneActor | undefined): actor is SceneActor & { speciesId: number; shiny: boolean } => actor !== undefined && 'speciesId' in actor;
/** PNJ de l'acteur (null : le joueur, un monstre ou un acteur inconnu). */
export const actorNpcId = (scene: Pick<Scene, 'actors'>, actorId: string): number | null => {
  const actor = scene.actors.find((entry) => entry.id === actorId);
  return actor && 'npcId' in actor ? actor.npcId : null;
};

export interface Scene {
  id: string;
  name: string;
  actors: SceneActor[];
  actions: SceneAction[];
}

export const SCENE_LIMITS = { maxPerMap: 30, maxActors: 15, maxActions: 80, maxPath: 30, maxWaitMs: 10_000, maxNameLength: 60 } as const;
export const SCENE_ID_PATTERN = /^[a-z0-9-]{4,40}$/;
export const ACTOR_ID_PATTERN = /^[a-z0-9-]{1,40}$/;

export const newSceneId = (): string => `s-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
export const newActorId = (): string => `a-${Math.random().toString(36).slice(2, 8)}`;

/** Les acteurs de scène ont un identifiant inférieur ou égal (les PNJ de la carte, positifs ; un dresseur venu défier le joueur, au-dessus). */
export const SCENE_ACTOR_ID_MAX = -1_000_000;

/** Durée d'un pas d'acteur : à son rythme (comme les PNJ), ou pressé. */
export const SCENE_STEP_MS = { normal: 420, fast: 240 } as const;

/** L'action concerne-t-elle un acteur, et lequel ? */
export const actorOf = (action: SceneAction): string | null => ('actor' in action ? action.actor : null);

/**
 * Pose de la carte que joue chaque acteur PNJ : la première de son PNJ parmi `poses` (celles de la carte, présentes
 * pour le joueur). Un acteur sans pose sur la carte doit apparaître.
 */
export function castOf<T extends { npcId: number }>(scene: Pick<Scene, 'actors'>, poses: readonly T[]): Map<string, T> {
  const cast = new Map<string, T>();
  for (const actor of scene.actors) {
    const pose = 'npcId' in actor ? poses.find((candidate) => candidate.npcId === actor.npcId) : undefined;
    if (pose) cast.set(actor.id, pose);
  }
  return cast;
}

/** Places de départ des acteurs PNJ qui se tiennent déjà sur la carte (voir castOf), pour stageAt. */
export function castPlaces(scene: Pick<Scene, 'actors'>, poses: readonly (Position & { npcId: number; dir: Direction })[]): Map<string, Position & { dir: Direction }> {
  return new Map([...castOf(scene, poses)].map(([actor, { x, y, dir }]) => [actor, { x, y, dir }]));
}

/**
 * Où sont les acteurs juste avant l'action `index` (toutes jouées : `actions.length`) : la case et le sens de chacun
 * des acteurs présents, d'après les actions précédentes et les places de départ (`start` : le joueur, les PNJ de la
 * carte). Pour l'éditeur : fantômes et départ des chemins.
 */
export function stageAt(scene: Pick<Scene, 'actions'>, index: number, start: ReadonlyMap<string, Position & { dir: Direction }> = new Map()): Map<string, Position & { dir: Direction }> {
  const places = new Map(start);
  for (const action of scene.actions.slice(0, index)) {
    if (action.type === 'appear') places.set(action.actor, { x: action.x, y: action.y, dir: action.dir });
    else if (action.type === 'vanish') places.delete(action.actor);
    else if (action.type === 'walk') {
      const last = action.path.at(-1);
      const from = places.get(action.actor);
      if (last && from) places.set(action.actor, { ...last, dir: from.dir });
    } else if (action.type === 'turn' && action.dir !== 'player') {
      const from = places.get(action.actor);
      if (from) places.set(action.actor, { ...from, dir: action.dir });
    }
  }
  return places;
}
